import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import SignaturePad from "../components/SignaturePad";
import { Logo } from "../lib/brand";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2, AlertCircle, Clock, Hash } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnvelopeSignPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sign/env/${token}`)
      .then((r) => {
        setDoc(r.data);
        setName(r.data.signer.name || "");
      })
      .catch((err) => setError(err?.response?.data?.detail || "Link unavailable"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your full name");
    if (!signature) return toast.error("Please add your signature");
    if (!consent) return toast.error("You must consent to sign electronically");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/sign/env/${token}`, {
        signature_image: signature,
        name,
        consent: true,
      });
      setDone(true);
      toast.success(r.data.completed ? "All signers complete — envelope executed." : "Signature submitted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#2A0F5A]" data-testid="envelope-sign-page">
      <Toaster position="top-right" toastOptions={{ className: "rounded-none border border-[#E8E3F0] bg-white text-[#2A0F5A]" }} />
      <header className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={28} />
            <span className="font-serif text-2xl">Marianne</span>
            <span className="label-eyebrow ml-2 hidden sm:inline">Sign</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#4A5568]">
            <ShieldCheck size={14} /> Secure signing
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {loading && (
          <div className="text-sm text-[#4A5568] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading document…
          </div>
        )}

        {error && (
          <div className="border border-[#EEC7C7] bg-[#F8E4E4] text-[#8a2121] px-6 py-5 text-sm" data-testid="sign-error">
            <AlertCircle size={16} className="inline mr-2" /> {error}
          </div>
        )}

        {done && (
          <div className="border border-[#BEDCC7] bg-[#DEEBE2] px-8 py-10 text-center" data-testid="sign-done">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-[#215a3b]" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl mb-2">Thank you — signature recorded.</h2>
            <p className="text-sm text-[#4A5568] max-w-md mx-auto">
              The sender has been notified. Once every signer has completed, the fully-executed document and the certificate of completion will be available to the owner.
            </p>
          </div>
        )}

        {doc && !done && !error && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Document pane */}
            <div className="lg:col-span-2">
              <div className="label-eyebrow mb-2">You are signing</div>
              <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-2">{doc.title}</h1>
              <div className="text-xs text-[#4A5568] mb-5 flex flex-wrap items-center gap-3">
                <span>From <b>{doc.owner_name}</b> ({doc.owner_email})</span>
                <span>·</span>
                <span className="font-mono flex items-center gap-1"><Hash size={10} /> {doc.document_sha256?.slice(0, 16)}…</span>
              </div>
              {doc.message && (
                <div className="border-l-2 border-[#C5A059] bg-[#C5A059]/10 px-4 py-3 text-sm italic mb-5">
                  "{doc.message}"
                </div>
              )}
              <div className="border border-[#E8E3F0] bg-white p-6 max-h-[55vh] overflow-y-auto whitespace-pre-wrap text-[14px] leading-[1.75] font-mono text-[#2A0F5A]/90">
                {doc.content}
              </div>

              {doc.signers_overview && doc.signers_overview.length > 1 && (
                <div className="mt-4 text-xs text-[#4A5568]">
                  Signers:{" "}
                  {doc.signers_overview.map((s, i) => (
                    <span key={i}>
                      {i > 0 && " · "}
                      <span className={s.status === "signed" ? "text-[#215a3b]" : ""}>
                        {s.name}{s.status === "signed" ? " ✓" : ""}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Sign pane */}
            <div className="lg:col-span-1">
              {!doc.is_next ? (
                <div className="border border-[#E8E3F0] bg-white p-5" data-testid="not-your-turn">
                  <Clock size={18} className="mb-2 text-[#C5A059]" />
                  <div className="font-serif text-xl mb-1">Not yet your turn</div>
                  <div className="text-xs text-[#4A5568]">
                    This envelope uses ordered signing. We'll notify you when it's your turn. You can bookmark this link.
                  </div>
                </div>
              ) : doc.already_signed ? (
                <div className="border border-[#BEDCC7] bg-[#DEEBE2] p-5">
                  <CheckCircle2 size={18} className="mb-2 text-[#215a3b]" />
                  <div className="font-serif text-xl mb-1">You've already signed.</div>
                  <div className="text-xs text-[#4A5568]">Thank you — nothing more to do here.</div>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-[#E8E3F0] bg-white p-5 space-y-4" data-testid="sign-form">
                  <div className="label-eyebrow">Your signature</div>
                  <SignaturePad
                    value={signature}
                    onChange={setSignature}
                    name={name}
                    onNameChange={setName}
                    testId="envelope-signature-pad"
                  />
                  <label className="flex items-start gap-2 text-xs text-[#4A5568]" data-testid="consent-checkbox">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      I consent to execute this document electronically under the IT Act, 2000.
                      I agree that my electronic signature is the legal equivalent of my handwritten signature.
                    </span>
                  </label>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
                    data-testid="submit-signature-btn"
                  >
                    {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Signing…</> : "Sign document"}
                  </Button>
                  <p className="text-[10px] text-[#4A5568] text-center">
                    Your IP address and timestamp are recorded for audit purposes.
                  </p>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
